import React, { useState, useEffect } from 'react';
import { apiService } from "../../../services/api";
import { usePermissions } from "../../../hooks/usePermissions";
import { ProtectedValue } from "../../../components/ProtectedValue";
import { isContratoVencido, isContratoAVencer } from "./contratoStatusUtils";

interface ContratosCardsProps {
  updateTrigger?: number;
  onCardClick?: (filtro: string) => void;
  filtroAtivo?: string | null;
}

const ContratosCards: React.FC<ContratosCardsProps> = ({ updateTrigger = 0, onCardClick, filtroAtivo }) => {
  const { hasPermission } = usePermissions();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalPeriodo, setTotalPeriodo] = useState<number>(0);
  const [totalRecebido, setTotalRecebido] = useState<number>(0);
  const [totalAReceber, setTotalAReceber] = useState<number>(0);
  const [totalVencidos, setTotalVencidos] = useState<number>(0);
  const [totalAVencer, setTotalAVencer] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContratos = async () => {
      try {
        setIsLoading(true);
        const response = await apiService.getContratos();

        if (response.success && response.data) {
          const contratos = response.data;

          // isContratoVencido / isContratoAVencer vêm de ./contratoStatusUtils
          // (data pura, sem fuso) para bater com o card do dashboard.

          // Separar contratos por categoria
          const contratosVencidos = contratos.filter(isContratoVencido);
          const contratosAVencer = contratos.filter(isContratoAVencer);
          const contratosAtivos = contratos.filter(contrato => 
            !isContratoVencido(contrato) && !isContratoAVencer(contrato)
          );
          
          // Calcular o total de contratos vencidos (situação OU data)
          const vencidos = contratosVencidos.reduce((sum, contrato) => {
            const valorPendente = (Number(contrato.valor_total) || 0) - (Number(contrato.valor_recebido) || 0);
            return sum + valorPendente;
          }, 0);

          // Calcular contratos a vencer nos próximos 3 meses
          const aVencer = contratosAVencer.reduce((sum, contrato) => {
            const valorPendente = (Number(contrato.valor_total) || 0) - (Number(contrato.valor_recebido) || 0);
            return sum + valorPendente;
          }, 0);
          
          // Calcular os totais gerais
          const periodo = contratos.reduce((sum, contrato) => sum + (Number(contrato.valor_total) || 0), 0);
          const recebido = contratos.reduce((sum, contrato) => sum + (Number(contrato.valor_recebido) || 0), 0);
          
          // A receber deve incluir apenas contratos ativos (não vencidos e não a vencer em breve)
          const aReceber = contratosAtivos.reduce((sum, contrato) => {
            const valorPendente = (Number(contrato.valor_total) || 0) - (Number(contrato.valor_recebido) || 0);
            return sum + valorPendente;
          }, 0);

          setTotalPeriodo(periodo);
          setTotalRecebido(recebido);
          setTotalAReceber(aReceber);
          setTotalVencidos(vencidos);
          setTotalAVencer(aVencer);
        } else {
          setError('Não foi possível obter os dados dos contratos');
        }
      } catch (err) {
        console.error('Erro ao buscar contratos:', err);
        setError('Erro ao carregar dados dos contratos');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContratos();
  }, [updateTrigger]);

  // Função para formatar valores monetários
  const formatCurrency = (value: number): string => {
    // Verificar se é um número válido
    if (isNaN(value) || value === null || value === undefined) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(0);
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Dados para os cards
  const cardData = [
    {
      title: 'SALDO DO CONTRATO',
      value: isLoading ? 'Carregando...' : formatCurrency(totalAReceber),
      bgClass: 'bg-purple-light',
      tooltip: 'Valores a receber de contratos ativos (não vencidos)',
      filtro: 'a-faturar'
    },
    {
      title: 'EMPENHADO',
      value: isLoading ? 'Carregando...' : formatCurrency(totalRecebido),
      bgClass: 'bg-success-light',
      filtro: 'recebidos'
    },
    {
      title: 'CONTRATO A VENCER (3 MESES)',
      value: isLoading ? 'Carregando...' : formatCurrency(totalAVencer),
      bgClass: 'bg-warning-light',
      tooltip: 'Contratos que vencem nos próximos 3 meses',
      filtro: 'a-vencer'
    },
    {
      title: 'CONTRATOS VENCIDOS',
      value: isLoading ? 'Carregando...' : formatCurrency(totalVencidos),
      bgClass: 'bg-danger-light',
      tooltip: 'Contratos vencidos por situação ou data',
      filtro: 'vencidos'
    },
    {
      title: 'TOTAL DE CONTRATOS',
      value: isLoading ? 'Carregando...' : formatCurrency(totalPeriodo),
      bgClass: 'bg-teal',
      filtro: 'total'
    }
  ];

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="financial-status-cards g-2 mb-5">
      {cardData.map((card, index) => (
        <div key={index} className="col-custom">
          <div 
            className={`campaign-box ${card.bgClass} h-100 ${filtroAtivo === card.filtro ? 'border border-primary border-3' : ''} ${onCardClick ? 'cursor-pointer' : ''}`}
            onClick={() => onCardClick && onCardClick(card.filtro)}
            style={{ 
              cursor: onCardClick ? 'pointer' : 'default',
              transition: 'all 0.3s ease',
              transform: filtroAtivo === card.filtro ? 'scale(1.02)' : 'scale(1)'
            }}
            title={card.tooltip || `Clique para filtrar por: ${card.title}`}
          >
            <div className="campaign-img">
              <p>{card.title}</p>
              {filtroAtivo === card.filtro && (
                <i className="ti ti-filter text-primary fs-5"></i>
              )}
            </div>
            <h2><ProtectedValue module="CONTRATOS" value={card.value} /></h2>
          </div>
        </div>
      ))}
    </div>
  );
};

// Renomeado para seguir padrão de nomenclatura PascalCase para componentes React
export default ContratosCards;